import type { CrmColumnItem } from "app/pages/sales-intelligence/sub-modules/management/types";

import type { CrmEnrichmentJobFilterDTO, CrmEnrichmentJobFilterType, Operator } from "../../types/types";

import type { ICrmEnrichmentFilterService } from "./types";

const DEFAULT_FILTER_VALUE: CrmEnrichmentJobFilterType = {
    id: 1,
    field: "",
    operator: "",
    singleValue: "",
    multiValue: [],
    selectedMultiValues: {},
    dataType: "Text",
    pickListItems: [],
};

export const createCrmEnrichmentFiltersService = (): ICrmEnrichmentFilterService => {
    const getDefaultFilter = () => DEFAULT_FILTER_VALUE;

    const isSingleSelectOperator = (operator: Operator | ""): boolean => {
        return operator === "greater" || operator === "lower";
    };

    const isNoValueOperator = (operator: Operator | ""): boolean => {
        return operator === "empty" || operator === "notEmpty";
    };

    const getSelectedMultiValuesLabels = (selectedMultiValues: Record<string, boolean>): readonly string[] => {
        return Object.keys(selectedMultiValues).filter((key) => selectedMultiValues[key]);
    };

    const getSelectedMultiValuesText = (selectedMultiValueLabels: string[]): string => {
        if (selectedMultiValueLabels.length === 0) {
            return "";
        }

        if (selectedMultiValueLabels.length > 1) {
            return `${selectedMultiValueLabels[0]} + ${selectedMultiValueLabels.length - 1}`;
        }

        return selectedMultiValueLabels[0];
    };

    const getSelectedMultiValuesFullText = (selectedMultiValues: Readonly<Record<string, boolean>>): string => {
        const selectedValues = getSelectedMultiValuesLabels(selectedMultiValues);
        return selectedValues.join(", ");
    };

    const getFieldValues = (
        { operator, pickListItems, singleValue, multiValue, selectedMultiValues }: CrmEnrichmentJobFilterType,
        columnData: CrmColumnItem | undefined,
    ): string => {
        if (isNoValueOperator(operator)) {
            return JSON.stringify("");
        }

        const isSingleOperator = isSingleSelectOperator(operator);

        if (pickListItems?.length) {
            const values = getSelectedMultiValuesLabels(selectedMultiValues);
            const pickListItemsOptions = Object.entries(columnData?.pickListItems || {});

            return JSON.stringify(values.map((value) => pickListItemsOptions.find(([_, v]) => v === value)?.[0]));
        }

        return isSingleOperator ? JSON.stringify(singleValue) : JSON.stringify(multiValue);
    };

    const createFilterDTO = (
        filter: CrmEnrichmentJobFilterType,
        columns: readonly CrmColumnItem[],
    ): CrmEnrichmentJobFilterDTO => {
        const { field, operator } = filter;

        const columnData = columns.find((account) => account.label === field);

        return {
            fieldName: columnData?.name || field,
            fieldValues: getFieldValues(filter, columnData),
            operator: operator as Operator,
        };
    };

    const createFilterFromDTO = (
        filter: CrmEnrichmentJobFilterDTO,
        columns: readonly CrmColumnItem[],
    ): Omit<CrmEnrichmentJobFilterType, "id"> => {
        const column = columns.find(({ name }) => name === filter.fieldName);

        const noValueOperator = isNoValueOperator(filter.operator);
        const isSingleOperator = isSingleSelectOperator(filter.operator);

        let selectedMultiValues: Record<string, boolean> = {};

        const pickListItemsOptions = column?.pickListItems || {};
        const pickListItems = Object.values(pickListItemsOptions);
        const hasPickListItems = pickListItems.length > 0;

        if (!noValueOperator) {
            const parsedFiledValues: string | string[] = JSON.parse(filter.fieldValues);

            if (hasPickListItems) {
                selectedMultiValues = Object.fromEntries(
                    (parsedFiledValues as string[]).map((value: string) => [pickListItemsOptions[value], true]),
                );
            }

            return {
                field: column?.label || "",
                operator: filter.operator,
                dataType: column?.dataType || "Text",
                singleValue: !hasPickListItems && isSingleOperator ? (parsedFiledValues as string) : "",
                multiValue: !hasPickListItems && !isSingleOperator ? (parsedFiledValues as string[]) : [],
                selectedMultiValues,
                pickListItems,
            };
        }

        return {
            field: column?.label || "",
            operator: filter.operator,
            dataType: column?.dataType || "Text",
            singleValue: "",
            multiValue: [],
            selectedMultiValues,
            pickListItems,
        };
    };

    const areFiltersValid = ({
        isApplyFiltersSelected,
        filters,
    }: {
        isApplyFiltersSelected: boolean;
        filters: readonly CrmEnrichmentJobFilterType[];
    }) => {
        if (!isApplyFiltersSelected) {
            return true;
        }

        return !filters.some(({ field, operator, singleValue, multiValue, selectedMultiValues, pickListItems }) => {
            if (!field || !operator) {
                return true;
            }

            if (isNoValueOperator(operator)) {
                return false;
            }

            let isValueValid;

            const isSingleOperator = isSingleSelectOperator(operator);

            if (pickListItems?.length) {
                isValueValid = getSelectedMultiValuesLabels(selectedMultiValues).length > 0;
            } else {
                isValueValid = isSingleOperator ? !!singleValue : !!multiValue.length;
            }

            return !isValueValid;
        });
    };

    const areFiltersDTOEqual = ({
        filters,
        initialFilters,
    }: {
        filters: readonly CrmEnrichmentJobFilterDTO[];
        initialFilters: readonly CrmEnrichmentJobFilterDTO[];
    }) => {
        if (filters.length !== initialFilters.length) {
            return false;
        }

        return initialFilters.every(
            (initialFilter, index) =>
                initialFilter.fieldName === filters[index].fieldName &&
                initialFilter.operator === filters[index].operator &&
                initialFilter.fieldValues === filters[index].fieldValues,
        );
    };

    const areFiltersEqualToInitial = ({
        filters,
        initialFilters,
        columns,
    }: {
        filters: readonly CrmEnrichmentJobFilterType[];
        initialFilters: readonly CrmEnrichmentJobFilterDTO[];
        columns: readonly CrmColumnItem[];
    }) => {
        if (filters.length !== initialFilters.length) {
            return false;
        }

        const filtersDTO = filters.map((filter) => createFilterDTO(filter, columns));

        return areFiltersDTOEqual({ initialFilters, filters: filtersDTO });
    };

    return {
        getDefaultFilter,
        isSingleSelectOperator,
        isNoValueOperator,
        getSelectedMultiValuesLabels,
        getSelectedMultiValuesText,
        getSelectedMultiValuesFullText,
        createFilterDTO,
        createFilterFromDTO,
        areFiltersValid,
        areFiltersEqualToInitial,
    };
};
